/* Hover previews for the pending-parcel tables: last 5 team notes / last 5 query messages. */
import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

const when = (iso) => { try { return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } };

const Lines = ({ items, empty }) => !items ? <Loader2 className="mx-auto h-4 w-4 animate-spin" />
  : !items.length ? <p className="text-[11px] text-slate-400">{empty}</p>
  : (<ul className="space-y-1.5">{items.map((e, i) => (
      <li key={i} className="text-[11px] leading-snug" data-testid={`hover-line-${i}`}>
        <span className="font-semibold text-slate-800">{e.by_name || "?"}</span>
        <span className="ml-1 text-[10px] text-slate-400">{when(e.at)}</span>
        <div className="whitespace-pre-wrap text-slate-700">{String(e.text || "").replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z?/g, (m) => when(m))}</div>
      </li>))}</ul>);

const Preview = ({ load, title, testId, children }) => {
  const [data, setData] = useState(null);
  return (
    <HoverCard openDelay={250} closeDelay={100} onOpenChange={(o) => { if (o) load().then(setData).catch(() => setData({ items: [] })); }}>
      <HoverCardTrigger asChild><span className="inline-block">{children}</span></HoverCardTrigger>
      <HoverCardContent className="w-80 p-3" data-testid={testId}>
        <div className="mb-1.5 text-xs font-bold text-slate-900">{data?.title || title}</div>
        {data?.sub && <div className="mb-1.5 text-[11px] text-slate-600">{data.sub}</div>}
        <Lines items={data?.items} empty="Nothing written yet" />
      </HoverCardContent>
    </HoverCard>
  );
};

export const NoteHover = ({ rowId, children }) => (
  <Preview title="Team notes" testId={`psr-note-hover-${rowId}`} load={async () => {
    const r = await axios.get(`${API}/order-register/parcel-notes/${rowId}`, { headers: getAuthHeader() });
    const n = r.data.notes || [];
    return { title: `Team notes (${n.length})`, items: n.slice(-5).reverse() };
  }}>{children}</Preview>
);

export const QueryHover = ({ rowId, children }) => (
  <Preview title="Query" testId={`psr-query-hover-${rowId}`} load={async () => {
    const r = await axios.get(`${API}/order-register/queries/by-row/${rowId}`, { headers: getAuthHeader() });
    const q = r.data.current;
    if (!q) return { title: "No query yet — click to raise one", items: [] };
    return { title: `${q.type} · ${q.status}${q.stale ? " · no reply 24h+" : ""}`,
             sub: q.outcome ? `Outcome: ${q.outcome}` : (q.products || []).join(", "),
             items: (q.entries || []).slice(-5).reverse() };
  }}>{children}</Preview>
);
